import React, { ComponentType } from 'react';

import {
  AccountsRefreshAuthTokenProvider,
  FormsRefreshTokenProvider,
  PaymentsRefreshAuthTokenProvider
} from '@nw/shared/data-access';

import {
  useUserAuthHandlerMachine,
  UseUserAuthHandlerMachineReturnType
} from '../../hooks/useUserAuthHandlerMachine';
import {
  UserAuthHandlerMachineSendType,
  UserAuthHandlerMachineStateType
} from '../../types/UserAuthHandlerMachineTypes';

interface WithUserAuthHandlerMachineProps {
  onSuccessfulLogin: (
    userAuthHandlerMachineState: UserAuthHandlerMachineStateType
  ) => void;
  onFailedLogin: () => void;
  localConfig?: any;
  shouldFetchFormsAuthDetails?: boolean;
  shouldFetchPaymentAuthDetails?: boolean;
  shouldCallUserProfileDetails?: boolean;
}

interface UserAuthHandlerMachineProps {
  userAuthHandlerMachineSend: UserAuthHandlerMachineSendType;
  userAuthHandlerMachineState: UserAuthHandlerMachineStateType;
  status: UseUserAuthHandlerMachineReturnType['status'];
  errors: UseUserAuthHandlerMachineReturnType['errors'];
}

const withProviders = (WrappedComponent: ComponentType<any>) => {
  const WithProviders = (props: any): React.ReactElement => {
    return (
      <AccountsRefreshAuthTokenProvider>
        <PaymentsRefreshAuthTokenProvider>
          <FormsRefreshTokenProvider>
            <WrappedComponent {...props} />
          </FormsRefreshTokenProvider>
        </PaymentsRefreshAuthTokenProvider>
      </AccountsRefreshAuthTokenProvider>
    );
  };

  return WithProviders;
};

const withUserAuthHandlerMachine = (
  WrappedComponent: ComponentType<
    WithUserAuthHandlerMachineProps & UserAuthHandlerMachineProps
  >
) => {
  const WithUserAuthHandlerMachine = (
    props: WithUserAuthHandlerMachineProps
  ) => {
    const {
      localConfig,
      shouldFetchFormsAuthDetails,
      shouldFetchPaymentAuthDetails,
      shouldCallUserProfileDetails
    } = props;

    const {
      userAuthHandlerMachineSend,
      userAuthHandlerMachineState,
      status,
      errors
    } = useUserAuthHandlerMachine(
      localConfig,
      shouldFetchFormsAuthDetails,
      shouldFetchPaymentAuthDetails,
      shouldCallUserProfileDetails
    );

    if (localConfig) {
      status.clientConfigDetailsApiStatus = 'success';
    }

    return (
      <WrappedComponent
        {...props}
        userAuthHandlerMachineSend={userAuthHandlerMachineSend}
        userAuthHandlerMachineState={userAuthHandlerMachineState}
        status={status}
        errors={errors}
      />
    );
  };

  return withProviders(WithUserAuthHandlerMachine);
};

export default withUserAuthHandlerMachine;
